<div class="container-fluid mt-4">
  <h2 class="mb-4">Avatar Yönetimi</h2>

  <div class="alert alert-success" *ngIf="successMessage">
    {{ successMessage }}
  </div>

  <div class="alert alert-danger" *ngIf="errorMessage">
    {{ errorMessage }}
  </div>

  <!-- Tab Navigation - Only for non-superusers -->
  <div class="mb-4" *ngIf="!isSuperUser">
    <div class="btn-group" role="group">
      <button 
        type="button" 
        class="btn" 
        [class.btn-primary]="activeTab === 'new-video'"
        [class.btn-outline-primary]="activeTab !== 'new-video'"
        (click)="switchTab('new-video')">
        <i class="fas fa-plus-circle me-2"></i>Yeni Video Talebi
      </button>
      <button 
        type="button" 
        class="btn" 
        [class.btn-primary]="activeTab === 'my-videos'"
        [class.btn-outline-primary]="activeTab !== 'my-videos'"
        (click)="switchTab('my-videos')">
        <i class="fas fa-video me-2"></i>Videolarım
      </button>
    </div>
  </div>

  <!-- New Video Tab Content -->
  <div *ngIf="activeTab === 'new-video' || isSuperUser" class="row">
    <!-- Superuser Video Upload Section -->
    <div class="col-md-4" *ngIf="isSuperUser">
      <div class="card mb-3">
        <div class="card-header bg-warning text-dark">
          <h5 class="mb-0">
            <i class="fas fa-user-shield me-2"></i>Admin Video Yükleme
          </h5>
        </div>
        <div class="card-body">
          <div class="alert alert-info">
            <small>
              <i class="fas fa-info-circle me-1"></i>
              <strong>Bilgi:</strong> Video yükledikten sonra talep "Tamamlandı" durumuna geçer ve aynı tabloda görünür.
            </small>
          </div>
          <div class="mb-3" *ngIf="pendingRequests.length > 0">
            <label for="requestSelect" class="form-label">Transcript Talebi Seçin *</label>
            <select 
              class="form-select" 
              id="requestSelect"
              [(ngModel)]="selectedRequestId"
              [class.is-invalid]="isSuperUser && pendingRequests.length > 0 && !selectedRequestId">
              <option [value]="null">-- Talep Seçin --</option>
              <option *ngFor="let request of pendingRequests" [value]="request.id">
                {{ request.avatar_name }} - {{ request.requester?.username }} ({{ request.company?.name || 'N/A' }}) - {{ request.request_date | date:'dd.MM.yyyy' }}
              </option>
            </select>
            <small class="form-text text-muted">
              Bu videoyu hangi transcript talebine yükleyeceğinizi seçin
            </small>
          </div>
          <div class="alert alert-warning" *ngIf="pendingRequests.length === 0">
            <small>
              <i class="fas fa-exclamation-triangle me-1"></i>
              Henüz beklemede olan talep bulunmuyor.
            </small>
          </div>
          <div class="mb-3">
            <label for="videoFile" class="form-label">Video Dosyası Seçin *</label>
            <input 
              type="file" 
              class="form-control" 
              id="videoFile"
              (change)="onVideoFileSelected($event)"
              accept="video/*">
            <small class="form-text text-muted">
              Kabul edilen formatlar: MP4, MPEG, MOV, AVI, WebM (Max: 500MB)
            </small>
          </div>
          <button 
            type="button" 
            class="btn btn-warning w-100"
            [disabled]="!selectedVideoFile || isLoading || (pendingRequests.length > 0 && !selectedRequestId)"
            (click)="uploadManualVideo()">
            <span *ngIf="!isLoading">
              <i class="fas fa-upload me-2"></i>Video Yükle
            </span>
            <span *ngIf="isLoading">
              <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Yükleniyor...
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- New Request Form (for non-superusers or superusers too) -->
    <div class="col-md-4" *ngIf="!isSuperUser">
      <div class="card">
        <div class="card-header bg-primary text-white">
          <h5 class="mb-0">Yeni Avatar Talebi</h5>
        </div>
        <div class="card-body">
          <form [formGroup]="avatarForm" (ngSubmit)="onSubmit()">
            <div class="mb-3">
              <label for="avatar_name" class="form-label">Avatar Seçin *</label>
              <select 
                class="form-select" 
                id="avatar_name" 
                formControlName="avatar_name"
                [class.is-invalid]="avatarForm.get('avatar_name')?.invalid && avatarForm.get('avatar_name')?.touched">
                <option value="">-- Avatar Seçin --</option>
                <option *ngFor="let avatar of avatarNames" [value]="avatar.name">
                  {{ avatar.name }} - {{ avatar.description }}
                </option>
              </select>
              <div class="invalid-feedback" *ngIf="avatarForm.get('avatar_name')?.invalid && avatarForm.get('avatar_name')?.touched">
                Lütfen bir avatar seçin
              </div>
            </div>

            <div class="avatar-preview-wrapper" *ngIf="selectedAvatarName">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <strong>{{ selectedAvatarName }}</strong>
              </div>
              <div class="avatar-variant-options mb-3">
                <div class="form-check form-check-inline" *ngFor="let variant of avatarVariantOptions">
                  <input
                    class="form-check-input"
                    type="radio"
                    [id]="'avatar-variant-' + variant.value"
                    name="avatarVariant"
                    [value]="variant.value"
                    [checked]="selectedVariant === variant.value"
                    [disabled]="!isVariantAvailable(selectedAvatarName, variant.value)"
                    (change)="onVariantChange(variant.value)">
                  <label
                    class="form-check-label"
                    [class.text-muted]="!isVariantAvailable(selectedAvatarName, variant.value)"
                    [for]="'avatar-variant-' + variant.value">
                    {{ variant.label }}
                  </label>
                </div>
              </div>

              <ng-container *ngIf="selectedMediaPath; else avatarMediaMissing">
                <video
                  *ngIf="isVideoSelected(); else avatarImagePreview"
                  class="avatar-preview-media"
                  [src]="selectedMediaPath"
                  controls>
                  Tarayıcınız bu videoyu desteklemiyor.
                </video>
                <ng-template #avatarImagePreview>
                  <img
                    class="avatar-preview-media"
                    [src]="selectedMediaPath"
                    [alt]="selectedAvatarName">
                </ng-template>
              </ng-container>
              <ng-template #avatarMediaMissing>
                <p class="text-muted small mb-0">
                  Seçilen varyant için medya bulunamadı.
                </p>
              </ng-template>
            </div>

            <div class="mb-3">
              <label for="transcript" class="form-label">Transcript Dosyası *</label>
              <input 
                type="file" 
                class="form-control" 
                id="transcript"
                (change)="onFileSelected($event)"
                accept=".txt,.pdf,.doc,.docx">
              <small class="form-text text-muted">
                Kabul edilen formatlar: TXT, PDF, DOC, DOCX (Max: 10MB)
              </small>
            </div>

            <div class="mb-3">
              <label for="notes" class="form-label">Notlar (İsteğe Bağlı)</label>
              <textarea 
                class="form-control" 
                id="notes" 
                formControlName="notes"
                rows="3"
                placeholder="Avatar için özel istekleriniz varsa belirtebilirsiniz...">
              </textarea>
            </div>

            <button 
              type="submit" 
              class="btn btn-primary w-100"
              [disabled]="avatarForm.invalid || !selectedFile || isLoading">
              <span *ngIf="!isLoading">Talep Oluştur</span>
              <span *ngIf="isLoading">
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                İşleniyor...
              </span>
            </button>
          </form>

          <div class="alert alert-info mt-3">
            <small>
              <strong>Süreç:</strong><br>
              1. Avatar ve transcript seçin<br>
              2. Talebiniz ekibimize iletilecek<br>
              3. Video hazırlandığında bildirim alacaksınız<br>
              4. Videonuzu indirebileceksiniz
            </small>
          </div>
        </div>
      </div>
    </div>

    <div class="col-md-8">
      <div class="card">
        <div class="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
          <h5 class="mb-0">Avatar Talepleri</h5>
          <select class="form-select form-select-sm w-auto" (change)="onStatusFilterChange($event)">
            <option *ngFor="let status of statusOptions" [value]="status.value">
              {{ status.label }}
            </option>
          </select>
        </div>
        <div class="card-body">
          <div class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>Avatar</th>
                  <th>Talep Eden</th>
                  <th *ngIf="isSuperUser">Şirket</th>
                  <th>Talep Tarihi</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let request of avatarRequests">
                  <td>{{ request.avatar_name }}</td>
                  <td>
                    <small>
                      {{ request.requester?.username }}<br>
                      <span class="text-muted">{{ request.requester?.email }}</span>
                    </small>
                  </td>
                  <td *ngIf="isSuperUser">
                    <small>{{ request.company?.name || '-' }}</small>
                  </td>
                  <td>{{ request.request_date | date:'dd.MM.yyyy HH:mm' }}</td>
                  <td>
                    <span class="badge" [ngClass]="getStatusClass(request.status)">
                      {{ getStatusLabel(request.status) }}
                    </span>
                  </td>
                  <td>
                    <div class="btn-group btn-group-sm" role="group">
                      <button 
                        class="btn btn-info btn-sm me-1"
                        *ngIf="request.transcript_file_path"
                        (click)="viewTranscript(request)"
                        title="Transcript'i Görüntüle">
                        <i class="fas fa-file-text"></i>
                      </button>
                      <button 
                        class="btn btn-secondary btn-sm me-1"
                        *ngIf="request.transcript_file_path"
                        (click)="downloadTranscript(request)"
                        title="Transcript'i İndir">
                        <i class="fas fa-download"></i>
                      </button>
                      <button 
                        class="btn btn-success btn-sm me-1"
                        *ngIf="request.status === 'completed' || request.status === 'delivered'"
                        (click)="downloadVideo(request)"
                        [disabled]="isDownloading(request.id)"
                        title="Videoyu İndir">
                        <ng-container *ngIf="!isDownloading(request.id); else requestDownloadInProgress">
                          <i class="fas fa-video"></i>
                        </ng-container>
                        <ng-template #requestDownloadInProgress>
                          <span class="d-flex align-items-center">
                            <i class="fas fa-spinner fa-spin me-1"></i>
                            <small>{{ formatDownloadProgress(request.id) }}</small>
                          </span>
                        </ng-template>
                      </button>
                      <button 
                        class="btn btn-danger btn-sm"
                        (click)="deleteRequest(request)"
                        title="Talebi Sil">
                        <i class="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr *ngIf="avatarRequests.length === 0">
                  <td [attr.colspan]="isSuperUser ? 7 : 6" class="text-center text-muted">
                    Henüz avatar talebi bulunmuyor
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <nav *ngIf="totalPages > 1" aria-label="Sayfalama">
            <ul class="pagination justify-content-center">
              <li class="page-item" [class.disabled]="currentPage === 1">
                <a class="page-link" (click)="previousPage()" style="cursor: pointer;">
                  Önceki
                </a>
              </li>
              <li class="page-item active">
                <span class="page-link">
                  {{ currentPage }} / {{ totalPages }}
                </span>
              </li>
              <li class="page-item" [class.disabled]="currentPage === totalPages">
                <a class="page-link" (click)="nextPage()" style="cursor: pointer;">
                  Sonraki
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>

  <!-- My Videos Tab Content - Only shows completed videos for companies -->
  <div *ngIf="activeTab === 'my-videos' && !isSuperUser">
    <div class="row">
      <!-- Videos List -->
      <div class="col-md-12">
        <div class="card">
          <div class="card-header bg-info text-white">
            <h5 class="mb-0">
              <i class="fas fa-film me-2"></i>Tamamlanan Videolar
            </h5>
          </div>
          <div class="card-body">
            <div class="alert alert-info" *ngIf="!isSuperUser">
              <small>
                <i class="fas fa-info-circle me-1"></i>
                Burada sadece tamamlanmış ve size teslim edilmiş avatar videolarınızı görebilirsiniz.
              </small>
            </div>
            
            <div class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th>Video Adı</th>
                    <th>Durum</th>
                    <th>Tamamlanma Tarihi</th>
                    <th *ngIf="isSuperUser">Talep Eden</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let video of uploadedVideos">
                    <td>
                      <i class="fas fa-video text-primary me-2"></i>
                      {{ video.avatar_name || 'Video' }}
                    </td>
                    <td>
                      <span class="badge" [ngClass]="getStatusClass(video.status)">
                        {{ getStatusLabel(video.status) }}
                      </span>
                    </td>
                    <td>{{ video.completion_date || video.upload_date | date:'dd.MM.yyyy HH:mm' }}</td>
                    <td *ngIf="isSuperUser">
                      <small>
                        {{ video.requester?.username || '-' }}<br>
                        <span class="text-muted">{{ video.requester?.email || '-' }}</span>
                      </small>
                    </td>
                    <td>
                      <div class="btn-group btn-group-sm" role="group">
                        <button 
                          class="btn btn-success btn-sm me-1"
                          (click)="downloadVideo(video)"
                          [disabled]="isDownloading(video.id)"
                          title="Videoyu İndir">
                          <ng-container *ngIf="!isDownloading(video.id); else videoDownloadInProgress">
                            <i class="fas fa-download me-1"></i>İndir
                          </ng-container>
                          <ng-template #videoDownloadInProgress>
                            <span class="d-flex align-items-center">
                              <i class="fas fa-spinner fa-spin me-1"></i>
                              <small>{{ formatDownloadProgress(video.id) }}</small>
                            </span>
                          </ng-template>
                        </button>
                        <button 
                          class="btn btn-danger btn-sm"
                          *ngIf="isSuperUser"
                          (click)="deleteUploadedVideo(video)"
                          title="Videoyu Sil">
                          <i class="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr *ngIf="uploadedVideos.length === 0">
                    <td [attr.colspan]="isSuperUser ? 5 : 4" class="text-center text-muted">
                      <i class="fas fa-film fa-3x mb-3 d-block"></i>
                      Henüz tamamlanmış video bulunmuyor
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div class="card mt-3" *ngIf="isLoading && activeTab === 'new-video'">
    <div class="card-body text-center">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Yükleniyor...</span>
      </div>
      <p class="mt-2">Veriler yükleniyor...</p>
    </div>
  </div>

  <!-- Transcript Modal -->
  <div class="modal fade" [class.show]="showTranscriptModal" [style.display]="showTranscriptModal ? 'block' : 'none'" 
       tabindex="-1" role="dialog" *ngIf="showTranscriptModal">
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fas fa-file-text me-2"></i>Transcript: {{ transcriptFileName }}
          </h5>
          <button type="button" class="btn-close" (click)="closeTranscriptModal()" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <ng-container *ngIf="currentTranscriptRequest?.notes">
            <div class="transcript-note mb-3">
              <div class="d-flex align-items-center mb-2">
                <i class="fas fa-sticky-note text-warning me-2"></i>
                <h6 class="mb-0">Talep Notu</h6>
              </div>
              <div class="note-content">
                {{ currentTranscriptRequest?.notes }}
              </div>
            </div>
          </ng-container>
          <div *ngIf="isLoading" class="text-center">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Yükleniyor...</span>
            </div>
            <p class="mt-2">Transcript yükleniyor...</p>
          </div>
          
          <div *ngIf="!isLoading && transcriptType === 'text'">
            <div class="mb-3">
              <button class="btn btn-sm btn-secondary" (click)="downloadTranscript(currentTranscriptRequest!)">
                <i class="fas fa-download me-1"></i>İndir
              </button>
            </div>
            <div class="border rounded p-3" style="max-height: 500px; overflow-y: auto; background-color: #f8f9fa;">
              <pre style="white-space: pre-wrap; word-wrap: break-word; font-family: inherit; margin: 0;">{{ transcriptContent }}</pre>
            </div>
          </div>
          
          <div *ngIf="!isLoading && (transcriptType === 'pdf' || transcriptType === 'word')">
            <div class="alert alert-info">
              <i class="fas fa-info-circle me-2"></i>
              Bu dosya türü tarayıcıda görüntülenemez. Lütfen dosyayı indirip görüntüleyin.
            </div>
            <div class="text-center">
              <button class="btn btn-primary btn-lg" (click)="downloadTranscript(currentTranscriptRequest!)">
                <i class="fas fa-download me-2"></i>Transcript'i İndir
              </button>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="closeTranscriptModal()">Kapat</button>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show" *ngIf="showTranscriptModal" (click)="closeTranscriptModal()"></div>
</div>